import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Star, Award, ChefHat } from "lucide-react";
import { api } from "@/lib/api";

export default function Founder() {
  const [founder, setFounder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/founder")
      .then((response) => {
        setFounder(response.data);
      })
      .catch((error) => {
        console.error("Failed to load founder:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-16 text-center">
        <div className="text-[#B7D8D5]">
          Loading Founder...
        </div>
      </div>
    );
  }

  if (!founder) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-16 text-center">
        <div className="text-red-300">
          Unable to load Founder information.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 py-10 md:py-16">
      <div className="glass rounded-[2rem] md:rounded-[3rem] p-6 md:p-16 grid md:grid-cols-2 gap-12 items-center">

        {/* Founder Image */}
        <div className="relative">
          <img
            src={founder.image || "/images/founder.png"}
            alt={`${founder.name}, Founder`}
            className="rounded-[2rem] w-full aspect-[4/5] object-cover shadow-2xl object-top"
          />

          <div className="absolute -bottom-5 -right-5 bg-[#B8E14A] text-white px-6 py-3 rounded-full text-xs uppercase tracking-widest">
            Founder
          </div>
        </div>

        {/* Founder Information */}
        <div>

          <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-3">
            Meet Our Founder
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl leading-tight mb-4">
            {founder.name}
          </h1>

          <div className="text-sm text-[#B7D8D5] mb-6">
            {founder.designation}
          </div>

          <p className="text-lg text-[#E6F4F2] leading-relaxed mb-4">
            {founder.description1}
          </p>

          <p className="text-lg text-[#E6F4F2] leading-relaxed mb-8">
            {founder.description2}
          </p>

          {/* Location */}
          <div className="flex items-center gap-2 text-sm text-[#E6F4F2] mb-8">
            <MapPin className="w-4 h-4 text-[#B8E14A]" />
            {founder.location}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">

            <Stat
              icon={ChefHat}
              label="Signature Flavours"
              value={founder.signature_flavours}
            />

            <Stat
              icon={Star}
              label="Natural Ingredients"
              value={founder.natural_ingredients}
            />

            <Stat
              icon={Award}
              label="Handcrafted Fresh"
              value={founder.handcrafted_fresh}
            />

          </div>

          {/* CTA */}
          <Link
            to={founder.cta_link || "/menu"}
            data-testid="founder-cta"
            className="btn-primary"
          >
            {founder.cta_text || "Taste the Craft →"}
          </Link>

        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/10 rounded-2xl p-4 border border-[#B8E14A]/30 min-w-0">

      <Icon className="w-5 h-5 text-[#B8E14A] mb-2" />

      <div className="font-heading text-2xl text-white break-words leading-tight">
        {value}
      </div>

      <div className="text-[10px] text-white/70 uppercase tracking-widest mt-1 leading-tight">
        {label}
      </div>

    </div>
  );
}